/**
 * Ask Addy v2 chat, built on assistant-ui primitives and styled with the sidebar's tokens.
 * Rendered by Chat.js in place of the legacy message list + composer when
 * REACT_APP_ASK_ADDY_V2_URL is set (local testing).
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import { toast } from "react-toastify";
import {
  ActionBarPrimitive,
  AssistantRuntimeProvider,
  AttachmentPrimitive,
  AuiIf,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAui,
  useAuiState,
} from "@assistant-ui/react";
import { MarkdownTextPrimitive } from "@assistant-ui/react-markdown";
import "@assistant-ui/react-markdown/styles/dot.css"; // pulsing caret while an answer streams
import { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";

// "~$41,465" means "about $41,465": single tildes must not strike through money (GFM's default).
const GFM = [[remarkGfm, { singleTilde: false }]];
import AskAddyV2, { defaultThreadId } from "../models/askAddyV2";
import AssistantAvatar from "./AssistantAvatar";
import { isVoiceSupported } from "./voice";
import { VoiceDock, useVoiceMode } from "./VoiceDock";
import PlusMenu from "./PlusMenu";
import { legacyChatIdOf, legacyChatToMessages, loadLegacyChat } from "./legacyChats";
import LendyIcon from "../components/lendy/LendyIcon";
import ScenarioQuestionsTray, { ScenarioQuestionsContext } from "./ScenarioQuestionsTray";
import { setBrowserResult, useAskAddyV2ActiveRun, useAskAddyV2History, useAskAddyV2Runtime, useReloadOnPick } from "./runtime";
import { NAVIGATE_LINK_SCHEME, appPathFromUrl, navigateFromLink, parseNavigateHref } from "../utils/guidedWalkthrough";
import { buildCitationViewerRequest, getCitationTargetOrigin, isPdfCitation } from "../helpers/citations";
import { citeNumberOf, rehypeCitePills } from "./citePills";
import { welcomeTitle } from "./greeting";
import { OPEN_PROFILE_EVENT } from "./agentNews";
import WelcomeAvatar from "./WelcomeAvatar";
import { getAnswerTurn, useAnswerFeedback } from "./answerFeedback";
import FeedbackReasonModal from "./FeedbackReasonModal";

/** The sidebar's browser-use loop (Chat.js), for use_my_browser; absent outside the extension. */
const BrowserUseContext = createContext(null);
/** Who is chatting ({ organizationId, userId }), for cards that read the loan officer's settings. */
const IdentityContext = createContext(null);
import "./AskAddyV2Thread.css";

/** This chat's thumbs up and down on answers, and how to save one (answerFeedback.js). */
const FeedbackContext = createContext(null);

/** The assistant as the organization names and pictures it (from Chat.js): on replies, the welcome and the composer. */
const AssistantContext = createContext({ name: "Addy AI", imageUrl: "" });

/** What the loan officer reads for each step the agent takes. Unlisted tools get a generic line. */
const STEP_LABELS = {
  "agent-loan-file": "Read the loan file",
  "agent-documents": "Checked documents",
  "agent-guidelines": "Looked up guidelines",
  "agent-underwriting": "Checked AUS and conditions",
  "agent-pipeline": "Checked pipeline and pricing",
  "agent-web-research": "Researched on the web",
  "agent-vm-operator": "Worked on the VM desktop",
  sandbox_run: "Ran a calculation",
  inspect_workbook: "Read the worksheet",
  fill_workbook: "Filled the worksheet",
  attach_file: "Prepared the download",
  vm_open_file: "Opened it on the VM desktop",
  vm_live_view: "Opened the live view",
  lookup_flood_zone: "Checked the FEMA flood map",
  web_search: "Searched the web",
  navigate_to: "Opened a page in Addy",
  use_my_browser: "Worked in your browser",
  gmail_search: "Searched your Gmail",
  gmail_read_thread: "Read an email thread",
  gmail_create_draft: "Saved a draft in your Gmail",
  gmail_send_email: "Sent an email from your Gmail",
  updateWorkingMemory: "Noted your preferences",
  remember: "Saved a note to memory",
  forget: "Removed a note from memory",
  search_past_conversations: "Searched earlier conversations",
  get_income_task_result: "Read Addy's income result",
  autopilot_create_job: "Set up a scheduled task",
  autopilot_update_job: "Changed a scheduled task",
  autopilot_delete_job: "Deleted a scheduled task",
  autopilot_list_jobs: "Looked at your scheduled tasks",
};
// Internal plumbing, or steps the chat shows another way (the [n] pills, choice buttons).
const HIDDEN_STEPS = new Set(["skill", "skill_read", "skill_search", "cite_sources", "ask_choice", "search_tools"]);
/** Listed or not: the server says (`hidden`); the local list covers older servers and reloaded history. */
const isHiddenStep = (part) => Boolean(part?.hidden) || HIDDEN_STEPS.has(part?.toolName);

const ACTION_LABELS = {
  update_loan_notes: "Update the loan notes",
  update_loan_details: "Update loan details",
  update_loan_stage: "Change the loan stage",
  update_task_status: "Change a checklist item's status",
  update_task_instructions: "Change a checklist item's instructions",
  rerun_task_with_instructions: "Re-run a checklist item",
  autopilot_create_job: "Set up a scheduled task",
  autopilot_update_job: "Change a scheduled task",
  autopilot_delete_job: "Delete a scheduled task",
  place_voice_call: "Call the borrower",
  send_email: "Send an email",
  gmail_send_email: "Send this email from your Gmail",
  request_documents: "Request documents from the borrower",
  fill_workbook: "Fill the worksheet",
};
const HIDDEN_ARGS = new Set(["organizationId", "userId", "cells", "sources", "outputs"]);

/** The tool's own name, readable: get_tasks_for_loan -> "Get tasks for loan". */
const humanize = (name = "") => {
  const prefixes = [
    ["mastra_workspace_computer_", "VM: "],
    ["mastra_workspace_", "Sandbox: "],
    ["browser_", "Browser: "],
    ["calc_", "Calculate: "],
  ];
  const [prefix, tag] = prefixes.find(([p]) => name.startsWith(p)) || ["", ""];
  // camelCase too: "updateWorkingMemory" -> "Update working memory".
  const words = name
    .slice(prefix.length)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  return tag + (words.charAt(0).toUpperCase() + words.slice(1) || "Step");
};

// A write shows its own name (e.g. "Update the loan notes"), never a generic lookup line — in
// reloaded history its approval card is gone, and a write must not read as a lookup.
const stepLabel = (name) => STEP_LABELS[name] || ACTION_LABELS[name] || humanize(name);
/** A step's label: the server's plain words when it sent them, else the local list. */
const labelOf = (part) => part?.label || stepLabel(part?.toolName);

// A step still running reads as happening now: "Checked pipeline and pricing" -> "Checking pipeline and pricing".
const ACTIVE_VERBS = {
  Change: "Changing",
  Checked: "Checking",
  Fill: "Filling",
  Filled: "Filling",
  Looked: "Looking",
  Opened: "Opening",
  Prepared: "Preparing",
  Ran: "Running",
  Read: "Reading",
  Request: "Requesting",
  Rerun: "Rerunning",
  Researched: "Researching",
  Saved: "Saving",
  Searched: "Searching",
  Send: "Sending",
  Update: "Updating",
  Worked: "Working",
};

const activeStepLabel = (name) => {
  const label = stepLabel(name);
  const [verb, ...rest] = label.split(" ");

  return ACTIVE_VERBS[verb] ? [ACTIVE_VERBS[verb], ...rest].join(" ") : label;
};

/** While a step is still running: the server's words, or the local name in the present tense. */
const runningLabel = (part) => part?.label || activeStepLabel(part?.toolName);

const formatValue = (v) => (typeof v === "number" ? v.toLocaleString("en-US") : typeof v === "string" ? v : JSON.stringify(v));
const formatSize = (b) => (!b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${Math.round(b / 1024)} KB` : `${(b / 1048576).toFixed(1)} MB`);

// ── icons (inline, so no extra dependency) ───────────────────────────────────
const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);
const CopyIcon = () => <Icon d={<><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></>} size={14} />;
const CheckIcon = () => <Icon d={<path d="M5 12l5 5L20 7" />} size={14} />;
const DocIcon = () => <Icon d={<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></>} size={14} />;
const DownloadIcon = () => <Icon d={<path d="M12 4v11m0 0l-4-4m4 4l4-4M5 20h14" />} size={16} />;
const ClockIcon = ({ size = 14 }) => <Icon d={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} size={size} />;
const SheetIcon = () => <Icon d={<><rect x="4" y="3" width="16" height="18" rx="2" /><path d="M4 9h16M4 15h16M10 3v18" /></>} size={14} />;

// ── parts ────────────────────────────────────────────────────────────────────
function FillTable({ cells, sources }) {
  return (
    <div className="aav2-fill-wrap">
      <table className="aav2-fill">
        <thead>
          <tr>
            <th>Cell</th>
            <th>Value</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(cells).map(([cell, value]) => (
            <tr key={cell}>
              <td className="aav2-mono">{cell}</td>
              <td className="aav2-num">{formatValue(value)}</td>
              <td className={sources?.[cell] ? "" : "aav2-missing"}>{sources?.[cell] || "no source given"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The exact email that goes out if the loan officer approves. */
export function EmailPreview({ email }) {
  const list = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
  return (
    <div className="aav2-email">
      <dl className="aav2-args">
        <div>
          <dt>To</dt>
          <dd>{list(email.to)}</dd>
        </div>
        {email.cc?.length ? (
          <div>
            <dt>Cc</dt>
            <dd>{list(email.cc)}</dd>
          </div>
        ) : null}
        {email.bcc?.length ? (
          <div>
            <dt>Bcc</dt>
            <dd>{list(email.bcc)}</dd>
          </div>
        ) : null}
        <div>
          <dt>Subject</dt>
          <dd>{email.subject}</dd>
        </div>
        {email.replyToMessageId ? (
          <div>
            <dt>Thread</dt>
            <dd>Reply in the existing conversation</dd>
          </div>
        ) : null}
      </dl>
      <div className="aav2-email-body">{email.body}</div>
    </div>
  );
}

function ApprovalCard({ toolName, args, display, approval, respondToApproval }) {
  const [busy, setBusy] = useState(false);
  const decided = approval.approved !== undefined;
  // The server's plain words first; never the tool's internal name.
  const label =
    display?.title ||
    ACTION_LABELS[toolName] ||
    (toolName?.startsWith("mastra_workspace_computer_") ? "Act on the VM desktop" : humanize(toolName));

  if (decided) {
    return (
      <div className="aav2-card aav2-approval" data-decided="true">
        <span className="aav2-card-title">{label}</span>
        <span className="aav2-outcome" data-approved={String(approval.approved)}>
          {approval.approved ? "✓ Approved" : "✕ Declined"}
        </span>
      </div>
    );
  }

  const decide = async (approved) => {
    setBusy(true);
    try {
      await respondToApproval(approved ? { approved: true } : { approved: false, reason: "The loan officer declined this action." });
    } catch (error) {
      // e.g. the conversation moved on; the card stays and says why in the console
      console.warn("[AskAddyV2] approval not applied", error);
      setBusy(false);
    }
  };

  const isEmail = toolName === "gmail_send_email";
  // The server's readable rows when it sent them; otherwise the arguments with readable labels.
  const rows = isEmail
    ? []
    : display
      ? display.fields.map((f) => [f.label, f.value])
      : Object.entries(args || {})
          .filter(([k]) => !HIDDEN_ARGS.has(k))
          .map(([k, v]) => [humanize(k), v]);
  const isFill = toolName === "fill_workbook" && args?.cells && typeof args.cells === "object";
  return (
    <div className="aav2-card aav2-approval">
      <div className="aav2-approval-head">
        <span className="aav2-badge">Needs your approval</span>
        <span className="aav2-card-title">{label}</span>
      </div>
      {rows.length > 0 && (
        <dl className="aav2-args">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{formatValue(v)}</dd>
            </div>
          ))}
        </dl>
      )}
      {isFill && <FillTable cells={args.cells} sources={args.sources} />}
      {isEmail && <EmailPreview email={args} />}
      <div className="aav2-card-actions">
        <button type="button" className="aav2-btn ghost" disabled={busy} onClick={() => decide(false)}>
          Decline
        </button>
        <button type="button" className="aav2-btn primary" disabled={busy} onClick={() => decide(true)}>
          {busy ? "Working…" : "Approve"}
        </button>
      </div>
    </div>
  );
}

/**
 * Addy asks to do something in the LO's own browser (use_my_browser). Nothing happens until they
 * allow it; then the sidebar's browser-use loop runs it (their browser asks again before anything
 * sensitive) and the agent continues with what happened.
 */
function BrowserTaskCard({ args, approval, respondToApproval }) {
  const browserUse = useContext(BrowserUseContext);
  const identity = useContext(IdentityContext);
  const [phase, setPhase] = useState("ask"); // ask | running | finished
  const [progress, setProgress] = useState("");
  const [confirmQuestion, setConfirmQuestion] = useState("");
  const [result, setResult] = useState(null);
  // Allowed or declined once: by the loan officer, or on its own when they don't want to be asked.
  const hasAnsweredRef = useRef(false);
  const decided = approval.approved !== undefined;

  const finish = async (outcome) => {
    setBrowserResult(approval.id, outcome);
    setResult(outcome);
    setPhase("finished");
    try {
      await respondToApproval(outcome.status === "declined" ? { approved: false, reason: "The loan officer did not allow it." } : { approved: true });
    } catch (error) {
      console.warn("[AskAddyV2] browser task result not delivered", error);
    }
  };

  const allow = async () => {
    if (hasAnsweredRef.current) return;
    hasAnsweredRef.current = true;
    setPhase("running");
    setProgress("Starting in your browser…");
    const outcome = await browserUse.run({
      goal: args?.goal || "",
      onProgress: (text) => {
        setProgress(text);
        setConfirmQuestion("");
      },
      onConfirm: (question) => setConfirmQuestion(question || "This step needs your confirmation."),
    });
    await finish(outcome);
  };

  const decline = () => {
    if (hasAnsweredRef.current) return;
    hasAnsweredRef.current = true;
    finish({ status: "declined" });
  };

  // "Working in your browser" switched off on the Approvals tab: start without asking.
  useEffect(() => {
    if (decided || !browserUse || !identity) return undefined;
    let isCurrent = true;
    AskAddyV2.approvals
      .list(identity)
      .then((settings) => {
        const browserSetting = settings.find(({ id }) => id === "browser");
        if (isCurrent && browserSetting && !browserSetting.askFirst) allow();
      })
      .catch(() => undefined); // without the settings it asks, as before
    return () => {
      isCurrent = false;
    };
  }, []); // eslint-disable-line -- once, when the card appears

  const outcomeText = (r) =>
    !r ? "" : r.status === "done" ? r.summary || "Done in your browser." : r.status === "declined" ? "Not allowed." : r.summary || "Stopped.";

  if (decided || phase === "finished") {
    return (
      <div className="aav2-card aav2-browser-done">
        <span className="aav2-card-title">
          {(result ? result.status === "declined" : approval.approved === false) ? "Use your browser" : "Used your browser"}
        </span>
        <div className="aav2-browser-goal">{args?.goal}</div>
        <span className="aav2-outcome" data-approved={String(result ? result.status === "done" : Boolean(approval.approved))}>
          {result ? outcomeText(result) : approval.approved ? "✓ Ran in your browser" : "✕ Not allowed"}
        </span>
      </div>
    );
  }

  return (
    <div className="aav2-card aav2-approval">
      <div className="aav2-approval-head">
        <span className="aav2-badge">{phase === "running" ? "Working in your browser" : "Needs your OK"}</span>
        <span className="aav2-card-title">Use your browser</span>
      </div>
      <div className="aav2-browser-goal">{args?.goal}</div>
      {args?.startUrl ? <div className="aav2-step-detail">Starts at {args.startUrl}</div> : null}
      {phase === "ask" && !browserUse ? (
        <div className="aav2-step-detail">This needs the Addy Chrome extension: open Ask Addy from the extension in your browser.</div>
      ) : null}
      {phase === "running" ? (
        <div className="aav2-step" data-status="running">
          <span className="aav2-step-dot" />
          <span className="aav2-shimmer">{progress}</span>
        </div>
      ) : null}
      {confirmQuestion ? (
        <div className="aav2-browser-confirm">
          <div>{confirmQuestion}</div>
          <div className="aav2-card-actions">
            <button type="button" className="aav2-btn ghost" onClick={() => browserUse.confirm(false)}>
              Cancel
            </button>
            <button type="button" className="aav2-btn primary" onClick={() => browserUse.confirm(true)}>
              Confirm and continue
            </button>
          </div>
        </div>
      ) : null}
      <div className="aav2-card-actions">
        {phase === "ask" ? (
          <>
            <button type="button" className="aav2-btn ghost" onClick={decline}>
              Don't allow
            </button>
            <button type="button" className="aav2-btn primary" disabled={!browserUse} onClick={allow}>
              Allow
            </button>
          </>
        ) : (
          <button type="button" className="aav2-btn ghost" onClick={() => browserUse.stop()}>
            Stop
          </button>
        )}
      </div>
    </div>
  );
}

/** One step of the work, by its heading only: what Lendy did, not the tool calls behind it. */
function Step({ toolName, label, hidden, status, result, isError }) {
  if (isHiddenStep({ toolName, hidden })) {
    return null;
  }

  const state = isError ? "error" : result !== undefined ? "complete" : status?.type === "running" ? "running" : "complete";

  return (
    <div className="aav2-step" data-status={state}>
      <span className="aav2-step-dot" />
      <span>
        {state === "running"
          ? `${runningLabel({ toolName, label })}…`
          : `${labelOf({ toolName, label })}${state === "error" ? " — didn't work" : ""}`}
      </span>
    </div>
  );
}

function FileCard({ file }) {
  return (
    <a className="aav2-card aav2-file" href={file.url} download={file.fileName}>
      <span className="aav2-file-icon">
        <DownloadIcon />
      </span>
      <span className="aav2-file-meta">
        <span className="aav2-file-name">{file.label || file.fileName}</span>
        <span className="aav2-file-sub">
          {file.fileName}
          {file.sizeBytes ? ` · ${formatSize(file.sizeBytes)}` : ""}
        </span>
      </span>
      <span className="aav2-file-action">Download</span>
    </a>
  );
}

const shortName = (name = "") => name.split("/").pop();

const NO_SOURCES = [];

/** The loan documents the answer being rendered cites, numbered as in its text: [1], [2]. */
const SourcesContext = createContext(NO_SOURCES);

/**
 * Opens a cited document. Inside the Addy web app a PDF opens in the app's own viewer, at the cited
 * page with the quoted words highlighted; anywhere else, and for other files, in a new tab.
 */
const openSource = ({ name, url, page, excerpt, mimeType }) => {
  const citation = { documentName: shortName(name), documentUrl: url, pageNumber: page, excerpt };
  const isPdf = mimeType === "application/pdf" || isPdfCitation(citation);
  const hostAppOrigin = window.parent !== window ? getCitationTargetOrigin() : "";
  if (hostAppOrigin && isPdf) {
    window.parent.postMessage({ key: "open-citation-pdf", value: buildCitationViewerRequest(citation) }, hostAppOrigin);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
};

/** "[2]" in an answer, as a numbered pill that opens the document. A number it doesn't cite stays text. */
const CitePill = ({ n, children }) => {
  const sources = useContext(SourcesContext);
  const source = sources.find((x) => x.n === n);
  if (!source?.url) {
    return <span>{children}</span>;
  }

  const { name, page } = source;
  const label = page ? `${shortName(name)}, page ${page}` : shortName(name);
  return (
    <Tooltip title={label} arrow>
      <button type="button" className="aav2-cite" aria-label={`Open ${label}`} onClick={() => openSource(source)}>
        {n}
      </button>
    </Tooltip>
  );
};

/** A clarifying question with known answers: each option sends itself as the reply. */
const timeOfDay = (date) => (date ? new Date(date).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");

/** Opens the profile on its Scheduled tasks tab (Chat.js listens). */
const openScheduledTasks = () => window.dispatchEvent(new CustomEvent(OPEN_PROFILE_EVENT, { detail: { tab: "schedule" } }));

/** A scheduled task was set up: what it is and when it runs, like a reminder app's confirmation. */
export function ScheduledTaskCard({ name, when }) {
  return (
    <div className="aav2-card aav2-scheduled-card">
      <div className="aav2-scheduled-card-head">
        <span className="aav2-scheduled-card-icon">
          <ClockIcon size={18} />
        </span>
        <span>
          <span className="aav2-scheduled-card-name">{name || "Scheduled task"}</span>
          <span className="aav2-scheduled-card-kind">Scheduled task{when ? ` · ${when}` : ""}</span>
        </span>
      </div>
      <button type="button" className="aav2-btn ghost aav2-scheduled-card-view" onClick={openScheduledTasks}>
        View scheduled task
      </button>
    </div>
  );
}

function Choice({ question, options }) {
  const aui = useAui();
  const isLast = useAuiState((s) => s.message.isLast);
  const running = useAuiState((s) => s.thread.isRunning);
  return (
    <div className="aav2-choice">
      {question ? <div className="aav2-choice-question">{question}</div> : null}
      {options.map((o, i) =>
        isLast && !running ? (
          // Sent to the thread itself: a Suggestion inside a message would target that message's (absent) edit composer.
          <button key={i} type="button" className="aav2-choice-option" onClick={() => aui.thread().append(o.label)}>
            <span className="aav2-choice-label">{o.label}</span>
            {o.detail ? <span className="aav2-choice-detail">{o.detail}</span> : null}
          </button>
        ) : (
          <div key={i} className="aav2-choice-option" data-disabled="true">
            <span className="aav2-choice-label">{o.label}</span>
            {o.detail ? <span className="aav2-choice-detail">{o.detail}</span> : null}
          </div>
        )
      )}
      {isLast && !running ? <div className="aav2-choice-hint">Or type your answer.</div> : null}
    </div>
  );
}

// react-markdown empties hrefs with custom schemes, which the agent's navigate: links use.
export const keepNavigateLinks = (url) => (url.startsWith(NAVIGATE_LINK_SCHEME) ? url : defaultUrlTransform(url));

/**
 * An agent's "[Open scenario](navigate:/scenarios/…)" link, or a full Addy app URL it wrote out
 * ("https://app.addy.so/loans/…?tabValue=processingChecklist"): a button that opens the page in the app.
 * Any other web link opens in a new tab. A plain link would load in this chat's iframe, replacing the chat.
 */
export function MarkdownLink({ href, children, node: _node, ...rest }) {
  const path = parseNavigateHref(href) ?? appPathFromUrl(href);
  if (!path) {
    const external = typeof href === "string" && /^https?:\/\//i.test(href);
    return (
      <a href={href} {...rest} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
        {children}
      </a>
    );
  }

  return (
    <a
      href={href}
      className="aav2-navigate-link"
      onClick={(event) => {
        event.preventDefault();
        navigateFromLink({
          path,
          parentWindow: window.parent && window.parent !== window ? window.parent : null,
          currentURL: new URLSearchParams(window.location.search).get("currentURL"),
        });
      }}
    >
      {children}
    </a>
  );
}

const MarkdownSpan = ({ node, children, ...rest }) => {
  const n = citeNumberOf({ ...rest, node });
  return n === null ? <span {...rest}>{children}</span> : <CitePill n={n}>{children}</CitePill>;
};

const MARKDOWN_COMPONENTS = { a: MarkdownLink, span: MarkdownSpan };
const REHYPE = [rehypeCitePills];

const Markdown = () => (
  <MarkdownTextPrimitive
    className="aav2-text aui-md"
    remarkPlugins={GFM}
    rehypePlugins={REHYPE}
    urlTransform={keepNavigateLinks}
    components={MARKDOWN_COMPONENTS}
  />
);

function AssistantPart({ part }) {
  if (part.type === "text") return <Markdown />;
  if (part.type === "reasoning") return null;
  if (part.type === "tool-call") {
    if (part.approval && part.toolName === "use_my_browser") return <BrowserTaskCard {...part} />;
    return part.approval ? <ApprovalCard {...part} /> : <Step {...part} />;
  }
  if (part.type === "data" && part.name === "v2-file") return <FileCard file={part.data} />;
  if (part.type === "data" && part.name === "v2-choice") return <Choice {...part.data} />;
  if (part.type === "data" && part.name === "v2-scheduled-task") return <ScheduledTaskCard {...part.data} />;
  // Shown as the answer's [n] pills (see CitePill).
  if (part.type === "data" && part.name === "v2-sources") return null;
  if (part.type === "data" && part.name === "v2-live-view") {
    return (
      <a className="aav2-live" href={part.data.url} target="_blank" rel="noreferrer">
        <span className="aav2-pulse" aria-hidden="true" /> Watch the VM desktop live
      </a>
    );
  }
  return null;
}

// ── thinking trace ───────────────────────────────────────────────────────────
/**
 * Steps and reasoning coalesce into one collapsible "thought" group. Approval cards stay out:
 * a write the loan officer must decide on can never be folded away inside the trace.
 * Module-level so the grouping is stable across renders.
 */
const GROUP_BY = (part) =>
  part.type === "reasoning" || (part.type === "tool-call" && !part.approval) ? ["group-thought"] : [];

const ChevronIcon = () => <Icon d={<path d="M9 6l6 6-6 6" />} size={14} />;
const SparkIcon = () => (
  <Icon d={<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" fill="currentColor" stroke="none" />} size={14} />
);

function ThoughtGroup({ group, children }) {
  const content = useAuiState((s) => s.message.content);
  const running = group.status?.type === "running" || group.counts?.running > 0;
  const steps = group.indices.map((i) => content[i]).filter((p) => p?.type === "tool-call" && !isHiddenStep(p));
  const current = [...steps].reverse().find((p) => p.result === undefined);
  const statusText = useAuiState((s) => s.message.metadata?.custom?.statusText || "");
  const lastIndex = Math.max(...group.indices);
  const hasAnswer = content.some((p, i) => i > lastIndex && p?.type === "text" && p.text?.trim());

  // Folded while it works: the header names the live action, and the LO opens the steps if they want them.
  const [open, setOpen] = useState(false);
  const isMessageRunning = useAuiState((s) => s.message.status?.type === "running");

  // Once the answer arrives (or the message ends), the work steps away: the answer is what they came for.
  if (hasAnswer || !isMessageRunning) return null;
  // While working, the header says what it's doing right now; between steps, that it's still at it.
  const label = current ? `${runningLabel(current)}…` : statusText && statusText !== "Thinking…" ? statusText : "Working…";

  return (
    <div className="aav2-thought" data-running={String(running)} data-open={String(open)}>
      <button
        type="button"
        className="aav2-thought-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="aav2-thought-icon">
          <SparkIcon />
        </span>
        <span className="aav2-thought-label aav2-shimmer">{label}</span>
        <span className="aav2-thought-chevron">
          <ChevronIcon />
        </span>
      </button>
      <div className="aav2-thought-body">
        <div className="aav2-thought-inner">{children}</div>
      </div>
    </div>
  );
}

/** Before anything streams: the server's status line, or a typing indicator. */
function Indicator() {
  const statusText = useAuiState((s) => s.message.metadata?.custom?.statusText || "");
  return statusText && statusText !== "Thinking…" ? (
    <div className="aav2-step" data-status="running">
      <span className="aav2-step-dot" />
      <span className="aav2-shimmer">{statusText}</span>
    </div>
  ) : (
    <div className="aav2-typing" aria-label="Thinking">
      <span />
      <span />
      <span />
    </div>
  );
}

// ── messages ─────────────────────────────────────────────────────────────────
function UserMessage() {
  const scheduled = useAuiState((s) => s.message.metadata?.custom?.scheduled);
  const createdAt = useAuiState((s) => s.message.createdAt);
  if (scheduled) return <ScheduledLabel {...scheduled} createdAt={createdAt} />;
  return (
    <MessagePrimitive.Root className="aav2-user">
      <MessagePrimitive.Attachments>
        {({ attachment }) => (
          <span className="aav2-attachment">
            {/\.(xlsx|xlsm|xls|csv)$/i.test(attachment.name || "") ? <SheetIcon /> : <DocIcon />}{" "}
            <span className="aav2-attachment-name">{attachment.name}</span>
          </span>
        )}
      </MessagePrimitive.Attachments>
      <AuiIf condition={(s) => s.message.content.some((p) => p.type === "text" && p.text)}>
        <div className="aav2-user-bubble">
          <MessagePrimitive.Parts />
        </div>
      </AuiIf>
    </MessagePrimitive.Root>
  );
}

/**
 * Where a scheduled task posted in the conversation: a small label above its answer, not a message
 * the loan officer typed.
 */
export function ScheduledLabel({ name, createdAt }) {
  const time = timeOfDay(createdAt);
  return (
    <MessagePrimitive.Root className="aav2-scheduled-label" aria-label={`Scheduled task${name ? `: ${name}` : ""}`}>
      <ClockIcon size={12} />
      <span>
        Scheduled task{name ? ` · ${name}` : ""}
        {time ? ` · ${time}` : ""}
      </span>
    </MessagePrimitive.Root>
  );
}

const TOAST_OPTIONS = { position: "bottom-right", autoClose: 5000, theme: "light" };

/**
 * Thumbs up or down on an answer, beside Copy, as on the web app's processing checklist tasks: up
 * turns green, down turns red and asks what didn't work. Clicking the chosen one again takes it back.
 */
function AnswerFeedback() {
  const aui = useAui();
  const feedback = useContext(FeedbackContext);
  const messageId = useAuiState((s) => s.message.id);
  const turn = useAuiState((s) => getAnswerTurn(s.thread.messages, s.message.id)?.turn ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAskingReason, setIsAskingReason] = useState(false);
  if (!feedback || turn === null) {
    return null;
  }

  const current = feedback.ratings[turn];
  const isPositive = current?.rating === "up";
  const isNegative = current?.rating === "down";

  const save = async (rating, reason) => {
    const answerTurn = getAnswerTurn(aui.thread().getState().messages, messageId);
    if (!answerTurn) {
      return;
    }

    setIsSaving(true);
    try {
      await feedback.rate({ ...answerTurn, rating, reason });
      toast.success(rating ? "Thank you for your feedback!" : "Feedback removed", TOAST_OPTIONS);
    } catch {
      toast.error(rating ? "Failed to submit feedback" : "Failed to remove feedback", TOAST_OPTIONS);
    } finally {
      setIsSaving(false);
    }
  };

  const thumbsDown = () => {
    if (isNegative) {
      save(null);

      return;
    }

    setIsAskingReason(true);
  };

  return (
    <>
      {isSaving ? (
        <span className="aav2-feedback-saving" aria-label="Saving feedback">
          <CircularProgress size={14} sx={{ color: "var(--text-tertiary)" }} />
        </span>
      ) : (
        <>
          <Tooltip title="Answer was useful" arrow>
            <button
              type="button"
              className={`aav2-feedback-btn aav2-feedback-btn--up${isPositive ? " is-selected" : ""}`}
              aria-label="Answer was useful"
              aria-pressed={isPositive}
              onClick={() => save(isPositive ? null : "up")}
            >
              <LendyIcon name="thumbUp" size={14} />
            </button>
          </Tooltip>
          <Tooltip title={isNegative && current.reason ? current.reason : "Answer was not useful"} arrow>
            <button
              type="button"
              className={`aav2-feedback-btn aav2-feedback-btn--down${isNegative ? " is-selected" : ""}`}
              aria-label="Answer was not useful"
              aria-pressed={isNegative}
              onClick={thumbsDown}
            >
              <LendyIcon name="thumbDown" size={14} />
            </button>
          </Tooltip>
        </>
      )}
      <FeedbackReasonModal
        open={isAskingReason}
        onClose={() => setIsAskingReason(false)}
        onSubmit={(reason) => {
          setIsAskingReason(false);
          save("down", reason);
        }}
      />
    </>
  );
}

function AssistantMessage() {
  const { name: assistantName, imageUrl } = useContext(AssistantContext);
  const sources =
    useAuiState((s) => s.message.content.find((p) => p.type === "data" && p.name === "v2-sources")?.data?.sources) ||
    NO_SOURCES;
  return (
    <MessagePrimitive.Root className="aav2-assistant">
      <AssistantAvatar imageUrl={imageUrl} size="reply" />
      <div className="aav2-assistant-body">
        <div className="aav2-name">{assistantName}</div>
        <SourcesContext.Provider value={sources}>
          <MessagePrimitive.GroupedParts groupBy={GROUP_BY} indicator="empty">
            {({ part, children }) =>
              part.type === "group-thought" ? (
                <ThoughtGroup group={part}>{children}</ThoughtGroup>
              ) : part.type === "indicator" ? (
                <Indicator />
              ) : (
                <AssistantPart part={part} />
              )
            }
          </MessagePrimitive.GroupedParts>
        </SourcesContext.Provider>
        <AuiIf condition={(s) => s.message.status?.type === "incomplete" && s.message.status?.reason === "cancelled"}>
          <div className="aav2-canceled">Response canceled.</div>
        </AuiIf>
        <MessagePrimitive.Error>
          <ErrorPrimitive.Root className="aav2-error">
            <ErrorPrimitive.Message />
            <AuiIf condition={(s) => s.message.isLast}>
              <ActionBarPrimitive.Reload className="aav2-btn ghost aav2-retry">Try again</ActionBarPrimitive.Reload>
            </AuiIf>
          </ErrorPrimitive.Root>
        </MessagePrimitive.Error>
        {/* Always in the page (shown by CSS on hover), so its row is reserved and hovering never shifts the chat. */}
        <ActionBarPrimitive.Root className="aav2-actions" hideWhenRunning autohide="never">
          <ActionBarPrimitive.Copy className="aav2-icon-btn" copiedDuration={1600}>
            <AuiIf condition={(s) => s.message.isCopied}>
              <CheckIcon /> Copied
            </AuiIf>
            <AuiIf condition={(s) => !s.message.isCopied}>
              <CopyIcon /> Copy
            </AuiIf>
          </ActionBarPrimitive.Copy>
          <AnswerFeedback />
        </ActionBarPrimitive.Root>
      </div>
    </MessagePrimitive.Root>
  );
}

// ── thread ───────────────────────────────────────────────────────────────────
const LOAN_SUGGESTIONS = [
  "Give me a quick status on this loan",
  "What's still outstanding before clear to close?",
  "Explain how qualifying income was calculated",
];
const SCENARIO_SUGGESTIONS = [
  "Summarize this scenario and its verdict",
  "What would make this scenario fit?",
  "Draft my reply to the broker with the follow-up questions",
];
const GENERAL_SUGGESTIONS = [
  "Which loans in my pipeline need attention this week?",
  "What are today's mortgage rates?",
  "Max loan for $11,500/mo income, $850 debts, 45% DTI at 6.75%?",
];

// The person's own suggestions are kept for the session, so each new chat shows them at once.
const STARTERS_KEY = "aav2-starters";

const readStoredStarters = (owner) => {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STARTERS_KEY) || "null");

    return stored?.owner === owner ? stored.starters : null;
  } catch {
    return null;
  }
};

/**
 * A general new chat's suggestions, written for this loan officer from what Addy remembers, its
 * agent and their day (ask-addy-agent /starters). The fixed ones stand in until they load, or if
 * the server has none.
 */
function useStarters(identity, isGeneral) {
  const owner = `${identity.organizationId}:${identity.userId}`;
  const [starters, setStarters] = useState(() => readStoredStarters(owner));

  useEffect(() => {
    if (!isGeneral || readStoredStarters(owner)) {
      return undefined;
    }

    let isCurrent = true;
    AskAddyV2.starters(identity)
      .then((loaded) => {
        if (!isCurrent || !loaded.length) {
          return;
        }
        setStarters(loaded);
        try {
          sessionStorage.setItem(STARTERS_KEY, JSON.stringify({ owner, starters: loaded }));
        } catch {
          // They still show for this chat.
        }
      })
      .catch(() => undefined);

    return () => {
      isCurrent = false;
    };
  }, [owner, isGeneral]); // eslint-disable-line

  return starters?.length ? starters : GENERAL_SUGGESTIONS;
}

const minutesAgo = (ms) => {
  const m = Math.round((Date.now() - ms) / 60000);
  return m < 1 ? "just now" : m === 1 ? "1 min ago" : `${m} min ago`;
};

/** An earlier question still being worked on (the sidebar was closed or the chat switched meanwhile). */
function StillWorking({ run, onStop }) {
  return (
    <div className="aav2-still-working" role="status">
      <span className="aav2-pulse" aria-hidden="true" />
      <span className="aav2-still-working-text">
        {run.scheduled ? "Working on your scheduled task" : "Still working on"} “{run.question.length > 90 ? `${run.question.slice(0, 89)}…` : run.question}” (started {minutesAgo(run.startedAt)}).
        The answer appears here when it’s ready — and you’ll get a notification if you leave.
      </span>
      <button type="button" className="aav2-btn ghost" onClick={onStop}>
        Stop
      </button>
    </div>
  );
}

const money = (n) => (typeof n === "number" ? `$${n.toLocaleString("en-US")}` : null);

/** One line about the open loan on the welcome screen: who, where, what, how many documents. */
function LoanLine({ identity, loanId }) {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    AskAddyV2.loanSummary(loanId, identity)
      .then((data) => !cancelled && setSummary(data))
      .catch(() => undefined); // the pill below still says which loan
    return () => {
      cancelled = true;
    };
  }, [loanId, identity.organizationId, identity.userId]); // eslint-disable-line
  if (!summary) return <span className="aav2-loan-pill">Asking about this loan</span>;
  const facts = [
    summary.borrowers?.join(" & "),
    summary.address,
    summary.purpose,
    money(summary.loanAmount),
    `${summary.documentCount} document${summary.documentCount === 1 ? "" : "s"}`,
  ].filter(Boolean);
  return <span className="aav2-loan-line">{facts.join(" · ")}</span>;
}

/** The open Scenario Center scenario's title and status, for the welcome line. */
const useScenarioSummary = (identity) => {
  const { scenarioId } = identity;
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setSummary(null);
    if (!scenarioId) return undefined;
    AskAddyV2.scenarioSummary(scenarioId, identity)
      .then((data) => !cancelled && setSummary(data))
      .catch(() => undefined); // the pill still says "this scenario"
    return () => {
      cancelled = true;
    };
  }, [scenarioId, identity.organizationId, identity.userId]); // eslint-disable-line
  return summary;
};

/** One line about the open scenario on the welcome screen, like the loan's. */
function ScenarioLine({ identity }) {
  const summary = useScenarioSummary(identity);
  if (!summary) return <span className="aav2-loan-pill">Asking about this scenario</span>;
  const isChecking = summary.status === "queued" || summary.status === "processing";
  return <span className="aav2-loan-line">{[summary.title, isChecking && "Still checking"].filter(Boolean).join(" · ")}</span>;
}

const GROUP_TITLES = { "borrower-info": "Borrower info", asset: "Assets", credit: "Credit", income: "Income", property: "Property", disclosures: "Disclosures", other: "Other" };
const groupTitle = (g) => GROUP_TITLES[g] || (g ? g.charAt(0).toUpperCase() + g.slice(1).replace(/-/g, " ") : "Other");

/** Pick the loan documents a question is about. Opens above the composer. */
function DocPicker({ identity, loanId, selected, onChange, onClose }) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    let cancelled = false;
    AskAddyV2.loanDocuments(loanId, identity)
      .then((rows) => !cancelled && setDocs(rows))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [loanId, identity.organizationId, identity.userId]); // eslint-disable-line
  const chosen = new Set(selected.map((d) => d.documentId));
  const toggle = (doc) =>
    onChange(chosen.has(doc.documentId) ? selected.filter((d) => d.documentId !== doc.documentId) : [...selected, { documentId: doc.documentId, name: doc.name }]);
  const shown = (docs || []).filter((d) => !filter || d.name.toLowerCase().includes(filter.toLowerCase()));
  const groups = [...new Set(shown.map((d) => d.group || "other"))];
  return (
    <div className="aav2-docpicker" role="dialog" aria-label="Pick documents">
      <div className="aav2-docpicker-head">
        <span className="aav2-card-title">Loan documents</span>
        <input className="aav2-docpicker-search" placeholder="Search" value={filter} onChange={(e) => setFilter(e.target.value)} />
      </div>
      <div className="aav2-docpicker-list">
        {error ? <div className="aav2-error">Couldn't load the documents: {error}</div> : null}
        {!docs && !error ? <div className="aav2-step" data-status="running"><span className="aav2-step-dot" /><span>Loading documents…</span></div> : null}
        {docs && !shown.length ? <div className="aav2-docpicker-empty">No documents match.</div> : null}
        {groups.map((g) => (
          <div key={g} className="aav2-docpicker-group">
            <div className="aav2-docpicker-group-title">{groupTitle(g)}</div>
            {shown
              .filter((d) => (d.group || "other") === g)
              .map((d) => (
                <label key={d.documentId} className="aav2-docpicker-row">
                  <input type="checkbox" checked={chosen.has(d.documentId)} onChange={() => toggle(d)} />
                  <span className="aav2-docpicker-name" title={d.name}>{shortName(d.name)}</span>
                </label>
              ))}
          </div>
        ))}
      </div>
      <div className="aav2-card-actions">
        <span className="aav2-docpicker-count">{selected.length} selected</span>
        {selected.length ? (
          <button type="button" className="aav2-btn ghost" onClick={() => onChange([])}>
            Clear
          </button>
        ) : null}
        <button type="button" className="aav2-btn primary" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

/**
 * A question typed while Addy is still answering waits here and is sent when the answer finishes.
 * Not sent after Stop (the LO may not want it any more): it goes back into the composer instead.
 */
function useQueuedQuestion() {
  const aui = useAui();
  const running = useAuiState((s) => s.thread.isRunning);
  const lastStatus = useAuiState((s) => s.thread.messages[s.thread.messages.length - 1]?.status);
  const [queued, setQueued] = useState(null);
  const wasRunning = useRef(running);
  useEffect(() => {
    const finished = wasRunning.current && !running;
    wasRunning.current = running;
    if (!finished || !queued) return;
    if (lastStatus?.type === "requires-action") return; // waiting on an approval card; send after it
    if (lastStatus?.type === "incomplete" && lastStatus?.reason === "cancelled") {
      aui.thread().composer().setText(queued);
      setQueued(null);
      return;
    }
    aui.thread().append(queued);
    setQueued(null);
  }, [running, lastStatus, queued]); // eslint-disable-line
  const queueCurrent = () => {
    const text = aui.thread().composer().getState().text.trim();
    if (!text) return;
    setQueued(text);
    aui.thread().composer().setText("");
  };
  return { queued, queueCurrent, clear: () => setQueued(null), running };
}

/**
 * A question asked from the web app (the loan page's Ask box) is sent here once this conversation
 * is open and idle. It shows the short question and asks the full prompt behind it.
 */
function usePendingQuestion(question, onSent, isBackgroundRunning) {
  const aui = useAui();
  const running = useAuiState((s) => s.thread.isRunning);
  const sentRef = useRef(null);
  useEffect(() => {
    if (!question || running || isBackgroundRunning || sentRef.current === question) return;
    sentRef.current = question;
    aui.thread().append({
      role: "user",
      content: [{ type: "text", text: question.displayQuery || question.query }],
      metadata: { custom: { query: question.query } },
    });
    onSent?.();
  }, [question, running, isBackgroundRunning]); // eslint-disable-line
}

/**
 * The composer: chips for the picked documents and attached files, then one line with +, the
 * input and send. + opens the classic chat's menu: Chat with your docs (on a loan) or Attach files.
 */
function Composer({ identity, loanId, documents, onDocumentsChange, onStop, voice, workLabel }) {
  const { name: assistantName, imageUrl } = useContext(AssistantContext);
  const [picking, setPicking] = useState(false);
  const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
  const plusButtonRef = useRef(null);
  // Always mounted: the primitive drops files picked after it unmounts, and the menu closes on click.
  const attachButtonRef = useRef(null);
  const { queued, queueCurrent, clear, running } = useQueuedQuestion();
  const attachmentCount = useAuiState((s) => s.composer.attachments.length);
  const hasChips = documents.length > 0 || attachmentCount > 0;
  const plusMenuItems = [
    loanId && { id: "docs", icon: "file", label: "Chat with your docs", hint: "Pick documents from this loan", onSelect: () => setPicking(true) },
    { id: "attach", icon: "upload", label: "Attach files", hint: "Upload from your device", onSelect: () => attachButtonRef.current?.click() },
  ].filter(Boolean);
  return (
    <>
      {queued ? (
        <div className="aav2-queued" role="status">
          <span className="aav2-queued-text">
            1 question queued: “{queued.length > 80 ? `${queued.slice(0, 79)}…` : queued}”. {assistantName} will answer it after the current one.
          </span>
          <button type="button" className="aav2-icon-btn" aria-label="Remove queued question" onClick={clear}>
            ✕
          </button>
        </div>
      ) : null}
      {picking && loanId ? (
        <DocPicker identity={identity} loanId={loanId} selected={documents} onChange={onDocumentsChange} onClose={() => setPicking(false)} />
      ) : null}
      {voice.phase !== "off" ? (
        <VoiceDock voice={voice} workLabel={workLabel} assistantName={assistantName} imageUrl={imageUrl} />
      ) : (
      <ComposerPrimitive.Root className="aav2-composer">
        {hasChips && (
          <div className="aav2-chips">
            {documents.map((d) => (
              <span key={d.documentId} className="aav2-chip" title={d.name}>
                <LendyIcon name="file" size={13} />
                <span className="aav2-chip-label">{shortName(d.name)}</span>
                <button
                  type="button"
                  className="aav2-chip-remove"
                  aria-label={`Stop using ${shortName(d.name)}`}
                  onClick={() => onDocumentsChange(documents.filter((x) => x.documentId !== d.documentId))}
                >
                  <LendyIcon name="x" size={11} />
                </button>
              </span>
            ))}
            <ComposerPrimitive.Attachments>
              {({ attachment }) => (
                <AttachmentPrimitive.Root className="aav2-chip aav2-chip--file">
                  <LendyIcon name={/^image\//.test(attachment.contentType || "") ? "image" : "file"} size={13} />
                  <span className="aav2-chip-label">
                    <AttachmentPrimitive.Name />
                  </span>
                  <AttachmentPrimitive.Remove className="aav2-chip-remove" aria-label="Remove attachment">
                    <LendyIcon name="x" size={11} />
                  </AttachmentPrimitive.Remove>
                </AttachmentPrimitive.Root>
              )}
            </ComposerPrimitive.Attachments>
          </div>
        )}
        <div className="aav2-composer-row">
          <button
            ref={plusButtonRef}
            type="button"
            className="aav2-tool"
            aria-label="Attach or chat with docs"
            aria-haspopup="menu"
            aria-expanded={isPlusMenuOpen}
            onClick={() => setIsPlusMenuOpen((isOpen) => !isOpen)}
          >
            <LendyIcon name="plus" />
          </button>
          <ComposerPrimitive.AddAttachment ref={attachButtonRef} hidden tabIndex={-1} aria-hidden="true" />
          {isPlusMenuOpen && (
            <PlusMenu anchor={plusButtonRef.current} items={plusMenuItems} onClose={() => setIsPlusMenuOpen(false)} />
          )}
          <ComposerPrimitive.Input
            className="aav2-input"
            placeholder={running ? "Ask a follow-up — it waits for this answer" : "Ask me anything or give me a task"}
            rows={1}
            submitMode={running ? "none" : "enter"}
            onKeyDown={(e) => {
              if (running && e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                queueCurrent();
              }
            }}
            autoFocus
          />
          {isVoiceSupported() && (
            <button type="button" className="aav2-tool" aria-label={`Talk to ${assistantName}`} title="Voice mode" onClick={voice.start}>
              <LendyIcon name="mic" />
            </button>
          )}
          <AuiIf condition={(s) => !s.thread.isRunning}>
            <ComposerPrimitive.Send className="aav2-send" aria-label="Send">
              <LendyIcon name="up" />
            </ComposerPrimitive.Send>
          </AuiIf>
          <AuiIf condition={(s) => s.thread.isRunning}>
            {/* Closing the sidebar leaves a run working; Stop is the only thing that ends it. */}
            <ComposerPrimitive.Cancel className="aav2-send aav2-send--stop" aria-label="Stop" onClick={onStop}>
              <i />
            </ComposerPrimitive.Cancel>
          </AuiIf>
        </div>
      </ComposerPrimitive.Root>
      )}
      {voice.error ? (
        <p className="aav2-voice-error" role="alert">
          {voice.error}
        </p>
      ) : null}
    </>
  );
}

function Thread({ identity, userName, loanId, documents, onDocumentsChange, backgroundRun, onStop, onStopBackground, onRunChange, pendingQuestion, onPendingQuestionSent }) {
  usePendingQuestion(pendingQuestion, onPendingQuestionSent, Boolean(backgroundRun?.running));
  const generalSuggestions = useStarters(identity, !loanId && !identity.scenarioId);
  const suggestions = loanId ? LOAN_SUGGESTIONS : identity.scenarioId ? SCENARIO_SUGGESTIONS : generalSuggestions;
  const { name: assistantName, imageUrl, isAgentMenuOpen, onOpenAgentMenu, onEmptyChange } = useContext(AssistantContext);
  const voice = useVoiceMode();
  const workLabel = useAuiState(selectWorkLabel);
  // A new chat centres the composer under the title, with the suggestions below it.
  const isEmpty = useAuiState((s) => s.thread.isEmpty);
  useEffect(() => {
    onEmptyChange?.(isEmpty);
  }, [isEmpty, onEmptyChange]);
  useEffect(() => () => onEmptyChange?.(false), [onEmptyChange]);
  return (
    <ComposerPrimitive.AttachmentDropzone asChild disabled={voice.phase !== "off"}>
    <ThreadPrimitive.Root className={isEmpty ? "aav2 aav2--empty" : "aav2"}>
      <div className="aav2-drop" aria-hidden="true">
        <div className="aav2-drop-card">
          <span className="aav2-drop-icon">
            <LendyIcon name="upload" size={22} />
          </span>
          <span className="aav2-drop-title">Drop files to attach</span>
          <span className="aav2-drop-sub">PDF, image, worksheet, CSV or XML, up to 100 MB each</span>
        </div>
      </div>
      <RunReporter backgroundRun={backgroundRun} onRunChange={onRunChange} isVoice={voice.phase !== "off"} />
      <ThreadPrimitive.Viewport className="aav2-viewport" autoScroll>
        <AuiIf condition={(s) => s.thread.isEmpty}>
          <div className="aav2-welcome">
            <WelcomeAvatar assistantName={assistantName} imageUrl={imageUrl} />
            {onOpenAgentMenu && (
              <button
                type="button"
                className="lendy-header-name aav2-welcome-agent"
                aria-label={`${assistantName}. Switch agent`}
                aria-haspopup="menu"
                aria-expanded={Boolean(isAgentMenuOpen)}
                onClick={onOpenAgentMenu}
              >
                <span className="lendy-header-name-text">{assistantName}</span>
                <LendyIcon name="down" size={13} />
              </button>
            )}
            {loanId && <LoanLine identity={identity} loanId={loanId} />}
            {identity.scenarioId && <ScenarioLine identity={identity} />}
            <h2 className="aav2-welcome-title">{welcomeTitle(userName)}</h2>
          </div>
        </AuiIf>

        <div className="aav2-messages">
          <ThreadPrimitive.Messages>
            {({ message }) =>
              message.role === "user" ? (
                <UserMessage />
              ) : (
                <AssistantMessage />
              )
            }
          </ThreadPrimitive.Messages>
        </div>

        <ThreadPrimitive.ScrollToBottom className="aav2-scroll-bottom">↓ New messages</ThreadPrimitive.ScrollToBottom>
      </ThreadPrimitive.Viewport>

      <div className="aav2-composer-wrap">
        {backgroundRun?.running && <StillWorking run={backgroundRun} onStop={onStopBackground} />}
        {identity.scenarioId ? <ScenarioQuestionsTray scenarioId={identity.scenarioId} /> : null}
        <Composer
          identity={identity}
          loanId={loanId}
          documents={documents}
          onDocumentsChange={onDocumentsChange}
          onStop={onStop}
          voice={voice}
          workLabel={workLabel}
        />
        {isEmpty && (
          <ul className="aav2-suggestions" aria-label="Suggestions">
            {suggestions.map((s) => (
              <li key={s}>
                <ThreadPrimitive.Suggestion prompt={s} send className="aav2-suggestion">
                  <LendyIcon name="chat" size={16} />
                  <span>{s}</span>
                </ThreadPrimitive.Suggestion>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ThreadPrimitive.Root>
    </ComposerPrimitive.AttachmentDropzone>
  );
}

/** The step the latest answer is on, or the server's status line before its first step. */
function selectWorkLabel(state) {
  const { messages } = state.thread;
  const last = messages[messages.length - 1];
  if (last?.role !== "assistant") {
    return "";
  }

  const step = [...(last.content || [])].reverse().find((p) => p.type === "tool-call" && p.result === undefined);

  return step ? `${runningLabel(step)}…` : last.metadata?.custom?.statusText || "";
}

const textOf = (message) =>
  (message?.content || [])
    .filter((p) => p.type === "text")
    .map((p) => p.text)
    .join(" ")
    .trim();

/**
 * Tells the sidebar what Lendy is doing in this conversation, for the header: running (and the
 * step it's on), waiting on the loan officer (an approval or a choice), or idle. A run that kept
 * going while the sidebar was closed counts as running.
 */
function RunReporter({ backgroundRun, onRunChange, isVoice }) {
  const isRunning = useAuiState((s) => s.thread.isRunning);
  const messages = useAuiState((s) => s.thread.messages);
  const statusText = useAuiState(selectWorkLabel);
  const last = messages[messages.length - 1];
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const waiting =
    last?.status?.type === "requires-action"
      ? "approval"
      : !isRunning && (last?.content || []).some((p) => p.type === "data" && p.name === "v2-choice")
        ? "answer"
        : "";
  const running = isRunning || !!backgroundRun?.running;
  const question = textOf(lastUser) || backgroundRun?.question || "";

  useEffect(() => {
    onRunChange?.({ isRunning: running, waiting, question, statusText, isVoice });
  }, [running, waiting, question, statusText, isVoice]); // eslint-disable-line
  // Leaving the chat (another conversation, or the profile over it) leaves nothing behind.
  useEffect(() => () => onRunChange?.({}), []); // eslint-disable-line
  return null;
}

function ChatWithHistory({ identity, userName, loanId, threadId, agentId, initialMessages, backgroundRun, onRunChange, pendingQuestion, onPendingQuestionSent }) {
  // Documents picked for this conversation's questions; they stay picked until cleared.
  const [documents, setDocuments] = useState([]);
  const runtime = useAskAddyV2Runtime({
    identity,
    loanId,
    threadId,
    agentId,
    documentIds: documents.map((d) => d.documentId),
    initialMessages,
  });
  const stop = () => AskAddyV2.cancelRun(threadId || defaultThreadId({ ...identity, loanId }), identity);
  const feedback = useAnswerFeedback(identity, threadId || defaultThreadId({ ...identity, loanId }), loanId);
  return (
    <FeedbackContext.Provider value={feedback}>
    <AssistantRuntimeProvider runtime={runtime}>
      <Thread
        identity={identity}
        userName={userName}
        loanId={loanId}
        documents={documents}
        onDocumentsChange={setDocuments}
        backgroundRun={backgroundRun}
        onStop={stop}
        onStopBackground={stop}
        onRunChange={onRunChange}
        pendingQuestion={pendingQuestion}
        onPendingQuestionSent={onPendingQuestionSent}
      />
    </AssistantRuntimeProvider>
    </FeedbackContext.Provider>
  );
}

const NO_LEGACY_CHAT = { loading: false, messages: [] };

/** The classic chat a v2 conversation continues, as messages to show above it; none for other conversations. */
const useLegacyChat = (userId, threadId) => {
  const legacyChatId = legacyChatIdOf(threadId);
  const [legacyChat, setLegacyChat] = useState({ chatId: null, messages: [] });

  useEffect(() => {
    if (!legacyChatId) {
      return undefined;
    }

    let cancelled = false;
    loadLegacyChat(userId, legacyChatId)
      .then((chat) => !cancelled && setLegacyChat({ chatId: legacyChatId, messages: legacyChatToMessages(chat?.messages) }))
      .catch(() => !cancelled && setLegacyChat({ chatId: legacyChatId, messages: [] }));
    return () => {
      cancelled = true;
    };
  }, [userId, legacyChatId]);

  if (!legacyChatId) {
    return NO_LEGACY_CHAT;
  }
  return legacyChat.chatId === legacyChatId ? { loading: false, messages: legacyChat.messages } : { loading: true, messages: [] };
};

/**
 * Loads a v2 conversation's earlier messages, then mounts a runtime for it. `threadId` picks the
 * conversation (a new chat or one from the history list); without it, the loan's default
 * conversation. Keyed by loan and thread, so switching either starts from that conversation.
 * `scenarioId` (the open Scenario Center scenario) works like `loanId` when no loan is open.
 * `pendingQuestion` ({ query, displayQuery }) is asked as soon as the conversation is ready.
 * `scenarioQuestions` ({ shared, send }) is the scenario page's follow-ups, shown above the composer.
 */
export default function AskAddyV2Chat({ organizationId, userId, userName, loanId, scenarioId, threadId, agentId, assistantName, assistantImageUrl, isAgentMenuOpen, onOpenAgentMenu, onEmptyChange, browserUse, onRunChange, pendingQuestion, onPendingQuestionSent, scenarioQuestions, openCount = 0 }) {
  const identity = { organizationId, userId, ...(scenarioId && !loanId ? { scenarioId } : {}) };
  // The new chat's welcome switches agent too, and tells the page when it shows (the header then hides the agent).
  const assistant = useMemo(
    () => ({ name: assistantName || "Addy AI", imageUrl: assistantImageUrl || "", isAgentMenuOpen, onOpenAgentMenu, onEmptyChange }),
    [assistantName, assistantImageUrl, isAgentMenuOpen, onOpenAgentMenu, onEmptyChange]
  );
  // A run started before this view opened may still be working; when it ends, reload the
  // conversation so its answer appears.
  const [reloadKey, setReloadKey] = useState(0);
  // Picking a chat in the list always opens it fresh, even the one already open: a click on the open
  // chat changed nothing, so it showed an empty or stale view until another chat was picked and back.
  useReloadOnPick(openCount, () => setReloadKey((k) => k + 1));
  const backgroundRun = useAskAddyV2ActiveRun(identity, loanId, threadId, () => setReloadKey((k) => k + 1));
  const history = useAskAddyV2History(identity, loanId, threadId, reloadKey);
  const legacyChat = useLegacyChat(userId, threadId);
  const initialMessages = useMemo(() => [...legacyChat.messages, ...history.messages], [legacyChat.messages, history.messages]);

  if (!organizationId || !userId) {
    return (
      <div className="aav2">
        <div className="aav2-welcome">
          <p className="aav2-welcome-sub">Waiting for your Addy account…</p>
        </div>
      </div>
    );
  }
  // Also waits for the assistant's name, so the welcome and composer never show a stand-in.
  if (history.loading || legacyChat.loading || !assistantName) {
    return (
      <div className="aav2">
        <div className="aav2-welcome">
          <div className="aav2-typing" aria-label="Loading conversation">
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    );
  }
  return (
    <AssistantContext.Provider value={assistant}>
    <IdentityContext.Provider value={identity}>
    <BrowserUseContext.Provider value={browserUse || null}>
    <ScenarioQuestionsContext.Provider value={scenarioQuestions || null}>
    <ChatWithHistory
      key={`${organizationId}:${userId}:${loanId || (identity.scenarioId ? `scenario-${identity.scenarioId}` : "general")}:${threadId || "default"}:${reloadKey}`}
      identity={identity}
      userName={userName}
      loanId={loanId}
      threadId={threadId}
      agentId={agentId}
      backgroundRun={backgroundRun}
      onRunChange={onRunChange}
      initialMessages={initialMessages}
      pendingQuestion={pendingQuestion}
      onPendingQuestionSent={onPendingQuestionSent}
    />
    </ScenarioQuestionsContext.Provider>
    </BrowserUseContext.Provider>
    </IdentityContext.Provider>
    </AssistantContext.Provider>
  );
}
